import { CircleUserRound, LogOut, RadioTower, Search, SlidersHorizontal, Users } from 'lucide-solid';
import { createMemo, createResource, createSignal, For, Show, type JSX } from 'solid-js';
import { BusyButton, CardSection, ConfirmModal, InlineNotice, Modal, ScreenHeader } from '../components/ui';
import { loadBuildCommit } from '../lib/buildInfo';
import { nonProductionEnvironment } from '../lib/consoleEnvironment';
import { can } from '../lib/permissions.generated';
import { ROLE_DESCRIPTIONS, permissionRowsAddedByRole } from '../lib/roleCopy';
import { useVenue } from '../state/VenueProvider';
import type { AssignableRole, VenueMember } from '../lib/types';
import './SettingsScreen.css';
import { TEST_IDS, testIdOf } from '../testIds';

/**
 * What the Remove button is about to undo — a person on the team, or an invitation nobody has taken up.
 * Both are "this address should not be on this list", and both end in the same confirmation.
 */
type Removable =
  | { kind: 'member'; uid: string; email: string | null }
  | { kind: 'invite'; inviteId: string; email: string | null };

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

/**
 * The role picker's options, in rank order. The line each one carries is the owner's own (2026-09-14) and
 * is checked against the permission table by `roleCopy.test.ts`, so it cannot outlive what the table says.
 */
const ROLE_OPTIONS: Array<{ value: AssignableRole; label: string }> = [
  { value: 'operator', label: 'Operator' },
  { value: 'manager', label: 'Manager' },
  { value: 'owner', label: 'Owner' },
];

const ROLE_LABELS: Record<string, string> = {
  operator: 'Operator',
  manager: 'Manager',
  owner: 'Owner',
};

export function SettingsScreen(): JSX.Element {
  const venue = useVenue();
  const actualCeiling = createMemo<number | undefined>(() => {
    const reported = venue.deviceDoc()?.maxIntensity
      ?? venue.lanStatus()?.engine?.maxIntensity
      ?? venue.lanStatus()?.maxIntensity;
    return Number.isFinite(Number(reported)) ? Number(reported) : undefined;
  });
  const [ceilingDraft, setCeilingDraft] = createSignal<number>();
  const [ceilingCandidate, setCeilingCandidate] = createSignal<number>();
  const [changeWord, setChangeWord] = createSignal('');
  const ceiling = createMemo<number | undefined>(() => ceilingDraft() ?? actualCeiling());
  const ceilingKnown = createMemo(() => ceiling() != null);
  const policy = createMemo(() => venue.selectedVenue()?.remoteLive ?? venue.roomStatus()?.remoteLive);
  const policyEnabled = createMemo(() => policy()?.enabled === true);
  const roomName = createMemo(() => venue.selectedRoom()?.displayName?.trim() || 'this room');
  const multipleRooms = createMemo(() => venue.rooms().length > 1);
  const policyTarget = createMemo(() => multipleRooms() ? "this venue's rooms" : roomName());
  const roleLabel = createMemo(() => ({
    staff: 'Nuance staff access',
    support: 'Nuance support access',
    owner: 'Owner access',
    manager: 'Manager access',
    operator: 'Operator access',
  })[venue.role()] ?? 'Operator access');
  const [buildCommit] = createResource(loadBuildCommit);
  const canEditPolicy = createMemo(() => can(venue.role(), 'remotelive.policy'));
  // Every card below is present or absent, never present-and-dead: an operator does not see a control
  // they cannot use (P1 / checklist Q14), and the cloud refuses the same rows it is gated on here.
  const canSetIntensityLimit = createMemo(() => can(venue.role(), 'room.intensity_limit'));
  const canManageMembers = createMemo(() => can(venue.role(), 'members.manage'));

  // ── the team ──────────────────────────────────────────────────────────────────────────────────────
  const [memberQuery, setMemberQuery] = createSignal('');
  const [inviteEmail, setInviteEmail] = createSignal('');
  const [inviteRole, setInviteRole] = createSignal<AssignableRole>('operator');
  const [removing, setRemoving] = createSignal<Removable>();
  const teamVenue = createMemo(() => (canManageMembers() && venue.user() ? venue.selectedVenue()?.venueId : undefined));
  const [members, membersControl] = createResource(teamVenue, () => venue.listVenueMembers());
  // A separate read from the roster, deliberately: an invite list the cloud cannot answer leaves the team
  // on screen rather than emptying it, and a venue with nobody waiting simply has no extra rows.
  const [invites, invitesControl] = createResource(teamVenue, () => venue.listVenueInvites());
  function refetchTeam(): void {
    void membersControl.refetch();
    void invitesControl.refetch();
  }
  const matches = (needle: string, ...fields: Array<string | null | undefined>): boolean =>
    !needle || fields.map((field) => field ?? '').join(' ').toLowerCase().includes(needle);
  const filteredMembers = createMemo(() => {
    const needle = memberQuery().trim().toLowerCase();
    return (members() ?? []).filter((member) => matches(needle, member.email, member.displayName));
  });
  const filteredInvites = createMemo(() => {
    const needle = memberQuery().trim().toLowerCase();
    return (invites() ?? []).filter((invite) => matches(needle, invite.email));
  });
  /** An owner is the only role that can hand the venue to someone else, so only they see that option. */
  const assignableRoles = createMemo(() =>
    ROLE_OPTIONS.filter((option) => option.value !== 'owner' || can(venue.role(), 'venue.transfer_ownership')));

  async function addMember(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const email = inviteEmail().trim();
    if (!email) return;
    // One call, two outcomes: someone who already has an account joins the team now, and someone who does
    // not is invited and waits. The toast says which, because the list underneath will look different.
    const outcome = await venue.runAction(
      'add-member',
      () => venue.inviteVenueMember(email, inviteRole()),
      (result) => result.status === 'pending' ? `${email} invited` : `${email} added`,
    );
    // The address stays in the field when the cloud refused it, so a typo can be corrected in place.
    if (!outcome) return;
    setInviteEmail('');
    refetchTeam();
  }

  async function changeMemberRole(member: VenueMember, next: AssignableRole): Promise<void> {
    if (next === member.role) return;
    // No toast: the row's own picker already shows the new role, so a toast would say it twice.
    await venue.runAction('member-role', () => venue.setMemberRole(member.uid, next));
    refetchTeam();
  }

  async function confirmRemoveMember(): Promise<void> {
    const target = removing();
    if (!target) return;
    await venue.runAction(
      'member-remove',
      () => target.kind === 'member' ? venue.removeVenueMember(target.uid) : venue.cancelVenueInvite(target.inviteId),
      `${target.email ?? ''} removed`.trim(),
    );
    setRemoving(undefined);
    refetchTeam();
  }

  function requestCeilingChange(): void {
    const next = ceiling();
    if (next == null || next === actualCeiling()) return;
    setCeilingCandidate(clamp(next, 0, 1));
    setChangeWord('');
  }

  function cancelCeilingChange(): void {
    setCeilingCandidate(undefined);
    setCeilingDraft(undefined);
    setChangeWord('');
  }

  async function confirmCeilingChange(): Promise<void> {
    const next = ceilingCandidate();
    if (next == null || changeWord() !== 'change') return;
    await venue.runAction(
      'save-ceiling',
      () => venue.setMaxIntensity(next),
      `Intensity limit set to ${Math.round(next * 100)}%`,
    );
    setCeilingCandidate(undefined);
    setCeilingDraft(undefined);
    setChangeWord('');
  }

  async function updatePolicy(enabled: boolean): Promise<void> {
    await venue.runAction(
      'remote-policy',
      () => venue.setRemoteLivePolicy(enabled),
      enabled ? 'Remote control enabled' : 'Remote control disabled',
    );
  }

  return (
    <>
      <ScreenHeader
        title="Settings"
        description="Manage room safety, remote access and your account. A dash means the controller has not reported a value yet."
      />

      <Show when={canSetIntensityLimit() && !venue.rigUnassigned()}>
        <div class="settings-control-grid settings-control-grid-single">
          <section class="surface setting-card">
            <div class="setting-card-heading">
              <span><SlidersHorizontal size={22} /></span>
              <div><h2>Intensity limit</h2><p>The maximum allowable brightness for this room</p></div>
            </div>
            <div class="ceiling-readout"><strong data-testid={TEST_IDS.ceilingValue}>{ceilingKnown() ? Math.round((ceiling() ?? 0) * 100) : '—'}</strong><Show when={ceilingKnown()}><span>%</span></Show></div>
            <input
              class="range"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={ceiling() ?? 0}
              disabled={!venue.controlsEnabled() || !ceilingKnown()}
              onInput={(event) => setCeilingDraft(event.currentTarget.valueAsNumber)}
              aria-label="Maximum intensity"
              data-testid={TEST_IDS.maxIntensity}
            />
            <div class="range-ends"><span>Dark</span><span>Full output</span></div>
            <button
              class="button button-primary"
              onClick={requestCeilingChange}
              disabled={!venue.controlsEnabled() || !ceilingKnown() || ceiling() === actualCeiling()}
              data-testid={TEST_IDS.saveMaxIntensity}
            >Save intensity limit</button>
          </section>
        </div>
      </Show>

      <Show when={canManageMembers()}>
        <CardSection
          class="members-card"
          testId={TEST_IDS.membersCard}
          bodyClass="member-list"
          bodyTestId={TEST_IDS.memberList}
          label="Members"
          heading={(
            <div class="setting-card-heading">
              <span><Users size={22} /></span>
              <div><h2>Members</h2></div>
            </div>
          )}
          tools={(
            <>
              <form class="member-add" onSubmit={(event) => void addMember(event)}>
                <input
                  type="email"
                  value={inviteEmail()}
                  onInput={(event) => setInviteEmail(event.currentTarget.value)}
                  placeholder="Email address"
                  aria-label="Email address"
                  autocomplete="off"
                  data-testid={TEST_IDS.memberEmail}
                />
                <select
                  value={inviteRole()}
                  onChange={(event) => setInviteRole(event.currentTarget.value as AssignableRole)}
                  aria-label="Role"
                  data-testid={TEST_IDS.memberAddRole}
                >
                  <For each={assignableRoles()}>{(option) => <option value={option.value}>{option.label}</option>}</For>
                </select>
                <BusyButton busyKey="add-member" type="submit" disabled={!inviteEmail().trim()} data-testid={TEST_IDS.memberAdd}>Add</BusyButton>
              </form>
              {/* Every role's line, always, rather than the chosen one's: a person choosing between three
                  things needs to read all three, and a line that appears and disappears reflows the form
                  under the picker that changed it. */}
              <dl class="role-descriptions" data-testid={TEST_IDS.memberRoleDescriptions}>
                <For each={assignableRoles()}>
                  {(option) => (
                    <>
                      <dt>{option.label}</dt>
                      <dd>
                        {ROLE_DESCRIPTIONS[option.value]}
                        <For each={permissionRowsAddedByRole(option.value)}>
                          {(label) => <span class="role-permission-row">{label}</span>}
                        </For>
                      </dd>
                    </>
                  )}
                </For>
              </dl>

              <Show when={(members() ?? []).length + (invites() ?? []).length > 8}>
                <label class="library-search">
                  <Search size={17} aria-hidden="true" />
                  <input
                    type="search"
                    value={memberQuery()}
                    onInput={(event) => setMemberQuery(event.currentTarget.value)}
                    placeholder="Search members"
                    aria-label="Search members"
                  />
                </label>
              </Show>
            </>
          )}
        >
          <For each={filteredMembers()}>
            {(member) => {
              const isOwner = () => member.role === 'owner';
              // A manager may not touch the owner in either direction, so for them the owner's row is
              // a name and a role and nothing else — exactly what the cloud would allow.
              const editable = () => !isOwner() || can(venue.role(), 'venue.transfer_ownership');
              return (
                <div data-testid={testIdOf('member', { uid: member.uid })}>
                  {/* Name over email in one cell, so every row is the same shape. A member with no
                      name is titled by their email, and it is not then repeated underneath. */}
                  <div class="member-identity">
                    <strong>{member.displayName?.trim() || member.email}</strong>
                    <Show when={member.displayName?.trim() && member.email}><small>{member.email}</small></Show>
                  </div>
                  <Show
                    when={editable()}
                    fallback={<em data-testid={testIdOf('memberRole', { uid: member.uid })}>{ROLE_LABELS[member.role ?? ''] ?? member.role}</em>}
                  >
                    <select
                      value={member.role ?? 'operator'}
                      onChange={(event) => void changeMemberRole(member, event.currentTarget.value as AssignableRole)}
                      aria-label={`Role for ${member.email ?? ''}`}
                      data-testid={testIdOf('memberRole', { uid: member.uid })}
                    >
                      <For each={assignableRoles()}>{(option) => <option value={option.value}>{option.label}</option>}</For>
                    </select>
                  </Show>
                  <Show when={editable()}>
                    <button
                      class="text-button danger-text"
                      onClick={() => setRemoving({ kind: 'member', uid: member.uid, email: member.email })}
                      data-testid={testIdOf('memberRemove', { uid: member.uid })}
                    >Remove</button>
                  </Show>
                </div>
              );
            }}
          </For>
          {/* Beneath the team, the addresses that have been invited and not taken up. Same row, and the
              same one action — the role is not a picker because an invite carries a role that is not a
              membership yet, and nothing here changes one. */}
          <For each={filteredInvites()}>
            {(invite) => {
              // The same bar the owner's row keeps: a manager cannot withdraw an invite that would make
              // an owner, so they are not offered the button — the cloud refuses that call too.
              const withdrawable = () => invite.role !== 'owner' || can(venue.role(), 'venue.transfer_ownership');
              const key = invite.email ?? invite.inviteId;
              return (
                <div data-testid={testIdOf('invite', { key })}>
                  <div class="member-identity">
                    <strong>{invite.email}</strong>
                    <small>Pending</small>
                  </div>
                  <em>{ROLE_LABELS[invite.role ?? ''] ?? invite.role}</em>
                  <Show when={withdrawable()}>
                    <button
                      class="text-button danger-text"
                      onClick={() => setRemoving({ kind: 'invite', inviteId: invite.inviteId, email: invite.email })}
                      data-testid={testIdOf('inviteRemove', { key })}
                    >Remove</button>
                  </Show>
                </div>
              );
            }}
          </For>
        </CardSection>
      </Show>

      <div class="settings-lower-grid">
        <Show when={canEditPolicy()}>
        <section class="surface policy-card">
          <div class="section-heading"><div><h2>Remote control</h2><p>Who can start a stream from Nuance</p></div><RadioTower size={21} /></div>
          <div class="policy-toggles">
            <label class="policy-toggle">
              <span><strong>Enable remote control of {policyTarget()} by Nuance?</strong><small>{multipleRooms()
                ? "This is a venue-wide setting, so it applies to every room here. When off, Nuance cannot start a remote stream."
                : `When off, Nuance cannot start a remote stream to ${roomName()}.`}</small></span>
              <input
                type="checkbox"
                role="switch"
                checked={policyEnabled()}
                disabled={!canEditPolicy() || !venue.user() || !venue.cloudReachable() || venue.actionBusy() === 'remote-policy'}
                onChange={(event) => void updatePolicy(event.currentTarget.checked)}
                data-testid={TEST_IDS.remoteControlEnabled}
              />
            </label>
          </div>
          <Show when={!venue.cloudReachable()}><InlineNotice tone="warning">The connection is offline. The saved setting will return automatically.</InlineNotice></Show>
        </section>
        </Show>

        <section class="surface account-card">
          <div class="section-heading"><div><h2>Account</h2><p>Your current console session</p></div><CircleUserRound size={21} /></div>
          {/* The build's own facts about this session ride the one line that is already about it: which
              commit is running, and — on a build that is NOT the venue's — which cloud it is driving. */}
          <div class="account-identity">
            <span>{(venue.user()?.email || 'L').slice(0, 1).toUpperCase()}</span>
            <div><strong>{venue.user()?.email || 'Local operator'}</strong><p>{roleLabel()} · {venue.user() ? (venue.user()?.emailVerified ? 'email verified' : 'verification pending') : 'controller pairing grant'}<Show when={buildCommit()}>{` · ${buildCommit()}`}</Show><Show when={nonProductionEnvironment}><span data-testid={TEST_IDS.accountEnvironment}>{` · ${nonProductionEnvironment}`}</span></Show></p></div>
          </div>
          <Show when={venue.user()}><button class="button button-secondary danger-button" onClick={() => void venue.runAction('sign-out', venue.signOut)}><LogOut size={17} /> Sign out</button></Show>
        </section>
      </div>

      {/* The owner's words, 2026-09-14. A destructive confirmation is his copy to write. */}
      <ConfirmModal
        open={removing() != null}
        title="Remove member?"
        detail="Are you sure you want to remove this member?"
        confirmLabel="Remove"
        tone="danger"
        busyKey="member-remove"
        onConfirm={() => void confirmRemoveMember()}
        onCancel={() => setRemoving(undefined)}
      />

      {/* The one confirmation that is not a yes/no: it asks for a word to be typed, so it keeps its own
          body — and puts its answer in the same footer band every other dialog uses. */}
      <Modal
        title="Change intensity limit?"
        open={ceilingCandidate() != null}
        onClose={cancelCeilingChange}
        footer={(
          <div class="modal-actions">
            <button class="button button-secondary" onClick={cancelCeilingChange}>Cancel</button>
            <BusyButton busyKey="save-ceiling" class="button button-danger" onClick={() => void confirmCeilingChange()} disabled={changeWord() !== 'change'} data-testid={TEST_IDS.intensityLimitConfirmAccept}>Confirm</BusyButton>
          </div>
        )}
      >
        <div class="simple-form typed-confirm" data-testid={TEST_IDS.intensityLimitConfirm}>
          <p>By changing the intensity limit, I understand that I am changing the maximum allowable brightness of lights in the room, which also might cause lights to run hotter than they do now.</p>
          <label>Type <strong>change</strong> to continue<input value={changeWord()} onInput={(event) => setChangeWord(event.currentTarget.value)} autocomplete="off" data-testid={TEST_IDS.intensityLimitConfirmWord} /></label>
        </div>
      </Modal>

    </>
  );
}
